import { useEffect } from "react";
import { isRouteErrorResponse, useNavigate, useRouteError } from "react-router";
import { ErrorPage } from "@/shared/components/ErrorPage";
import { captureError } from "@/shared/lib/sentry";

/**
 * React Router `errorElement`. With a data router (`createBrowserRouter`),
 * errors thrown while rendering, loading, or acting within a route are caught
 * by the router before they reach any React error boundary — so a route-level
 * element is the only way to catch them. Reports to Sentry and renders the
 * shared ErrorPage.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();

  useEffect(() => {
    captureError(error);
  }, [error]);

  const isNotFound = isRouteErrorResponse(error) && error.status === 404;

  function handleGoBack() {
    // navigate(-1) is a no-op with no prior history; fall back to the app home.
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/app");
    }
  }

  return (
    <ErrorPage
      title={isNotFound ? "Page not found" : undefined}
      description={
        isNotFound
          ? "We couldn't find the page you were looking for. It may have moved or never existed."
          : undefined
      }
      onTryAgain={() => navigate(0)}
      onGoBack={handleGoBack}
    />
  );
}
